import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { loadTaskCategory } from 'utils/taskCategoryStorage';

export interface InputState {
  selectedInputId: string | null;
  /**
   * The tasks grid's input filter (a STEP_CATEGORY_FILTERS key), or null for
   * all inputs. Kept when switching plans, and shared so Live totals can
   * follow it (OP-2535).
   */
  tasksStepCategoryKey: string | null;
  /** True while the tasks grid is on screen — Live totals follow its filter only then. */
  isTasksGridOpen: boolean;
  /** The Tasks grid's Plan/Actual tab, kept across plans for the session (OP-2556). */
  activeTaskCategory: InputTaskCategory;
  /** The open plan has edits not yet saved (mirrored from the workspace for the copy tool). */
  hasUnsavedChanges: boolean;
  /** Bumped when the open plan is saved elsewhere (the copy tool), so its draft reloads. */
  inputDraftReloadKey: number;
}

const initialState: InputState = {
  selectedInputId: null,
  tasksStepCategoryKey: null,
  isTasksGridOpen: false,
  activeTaskCategory: loadTaskCategory(),
  hasUnsavedChanges: false,
  inputDraftReloadKey: 0,
};

const inputSlice = createSlice({
  name: 'input',
  initialState,
  reducers: {
    setSelectedInputId: (state, action: PayloadAction<string | null>) => {
      state.selectedInputId = action.payload;
    },
    setTasksStepCategoryKey: (state, action: PayloadAction<string | null>) => {
      state.tasksStepCategoryKey = action.payload;
    },
    setTasksGridOpen: (state, action: PayloadAction<boolean>) => {
      state.isTasksGridOpen = action.payload;
    },
    setActiveTaskCategory: (
      state,
      action: PayloadAction<InputTaskCategory>
    ) => {
      state.activeTaskCategory = action.payload;
    },
    setHasUnsavedInputChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    reloadInputDraft: (state) => {
      state.inputDraftReloadKey += 1;
    },
    // Keeps the session Plan/Actual tab and the workspace-owned unsaved flag.
    clearInputSelection: (state) => ({
      ...initialState,
      activeTaskCategory: state.activeTaskCategory,
      hasUnsavedChanges: state.hasUnsavedChanges,
    }),
  },
});

export const {
  setSelectedInputId,
  setTasksStepCategoryKey,
  setTasksGridOpen,
  setActiveTaskCategory,
  setHasUnsavedInputChanges,
  reloadInputDraft,
  clearInputSelection,
} = inputSlice.actions;

export default inputSlice.reducer;
